/**
 * Cadence vocabulary and calendar rules shared by the settings UI, the tRPC
 * API and every sender (engine triggers + the jobs digest).
 *
 * Deliberately dependency-free (no luxon) so `@graftus/schema` stays a leaf
 * package — weekday resolution goes through `Intl`, which every runtime we
 * ship to already has.
 */

export const NOTIFICATION_FREQUENCIES = [
  "off",
  "instant",
  "daily",
  "weekly",
] as const;

export type NotificationFrequency = (typeof NOTIFICATION_FREQUENCIES)[number];

export function isNotificationFrequency(
  value: unknown,
): value is NotificationFrequency {
  return (
    typeof value === "string" &&
    (NOTIFICATION_FREQUENCIES as readonly string[]).includes(value)
  );
}

/**
 * Fallback for businesses with no `notificationTimeZone` on their doc. Every
 * cron in the engine and the jobs package used to hardcode this, so it is also
 * what the schedules themselves still fire on.
 */
export const DEFAULT_BUSINESS_TIME_ZONE = "Europe/Stockholm";

const WEEKDAY_NUMBER: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

/** ISO weekday (Mon = 1 … Sun = 7) for `date` as observed in `timeZone`. */
export function weekdayInTimeZone(date: Date, timeZone: string): number {
  let label: string;
  try {
    label = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
    }).format(date);
  } catch {
    // An unknown IANA id would otherwise take down the whole send loop for one
    // business with a typo'd timezone. Fall back rather than throw.
    label = new Intl.DateTimeFormat("en-US", {
      timeZone: DEFAULT_BUSINESS_TIME_ZONE,
      weekday: "short",
    }).format(date);
  }
  return WEEKDAY_NUMBER[label] ?? 1;
}

export function isWeekendInTimeZone(date: Date, timeZone: string): boolean {
  return weekdayInTimeZone(date, timeZone) >= 6;
}

export function isMondayInTimeZone(date: Date, timeZone: string): boolean {
  return weekdayInTimeZone(date, timeZone) === 1;
}

/** `YYYY-MM-DD` for `date` as observed in `timeZone` — used for idempotency keys. */
export function isoDateInTimeZone(date: Date, timeZone: string): string {
  const format = (tz: string) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  try {
    return format(timeZone);
  } catch {
    return format(DEFAULT_BUSINESS_TIME_ZONE);
  }
}

/**
 * Whether a recipient on `frequency` is due at `at`, evaluated in the
 * business's timezone.
 *
 * - `off`     — never.
 * - `instant` — event-driven, no calendar gate.
 * - `daily`   — weekdays only. Weekend content is skipped, not held for Monday.
 * - `weekly`  — Monday only.
 *
 * Callers still own the cadence of their cron; this only decides whether a
 * given tick is a delivery moment for this recipient.
 */
export function shouldDeliverForFrequency({
  frequency,
  at,
  timeZone,
}: {
  frequency: NotificationFrequency;
  at: Date;
  timeZone: string;
}): boolean {
  switch (frequency) {
    case "off":
      return false;
    case "instant":
      return true;
    case "daily":
      return !isWeekendInTimeZone(at, timeZone);
    case "weekly":
      return isMondayInTimeZone(at, timeZone);
  }
}
